import React, { cloneElement, isValidElement, useCallback, useContext, useEffect, useRef, } from 'react';
import { observer } from 'mobx-react-lite';
import { action, get, remove, set } from 'mobx';
import classNames from 'classnames';
import defer from 'lodash/defer';
import { Size } from '../../../lib/_util/enum';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import measureScrollbar from '../../../lib/_util/measureScrollbar';
import TableCell from './TableCell';
import TableVirtualCell from './TableVirtualCell';
import TableContext from './TableContext';
import ExpandIcon from './ExpandIcon';
import { ColumnLock, DragColumnAlign, HighLightRowType, SelectionMode } from './enum';
import { findCell, getColumnKey, getColumnLock, isDisabledRow, isSelectedRow, isStickySupport } from './utils';
import { CUSTOMIZED_KEY, DRAG_KEY, EXPAND_KEY } from './TableStore';
import { RecordStatus } from '../data-set/enum';
import ResizeObservedRow from './ResizeObservedRow';
import Spin from '../spin';
import useComputed from '../use-computed';
import { iteratorSome } from '../_util/iteratorUtils';
function getGroupByPath(group, groupPath) {
    const { subGroups } = group;
    if (groupPath.length) {
        const path = groupPath.shift();
        if (path && subGroups.length) {
            const subGroup = subGroups.find(sub => sub.value === path[0].value);
            if (subGroup) {
                return getGroupByPath(subGroup, groupPath);
            }
            return undefined;
        }
    }
    return group;
}
function getRecord(columnGroup, groupPath, index, record) {
    if (index !== undefined && groupPath) {
        const { headerGroup } = columnGroup;
        if (headerGroup) {
            const group = getGroupByPath(headerGroup, groupPath.slice());
            if (group) {
                return group.totalRecords[index];
            }
            return undefined;
        }
    }
    return record;
}
const VIRTUAL_HEIGHT = '__VIRTUAL_HEIGHT__';
const TableRow = function TableRow(props) {
    const { record, hidden, index, virtualIndex, headerGroupIndex, provided, snapshot, isDragDisabled, className, lock, columnGroups, children, groupPath, expandIconColumnIndex, metaData, style: propStyle, intersectionRef, inView = true, virtualHeight, isFixedRowHeight, virtualCell, columnsInView = true, } = props;
    const context = useContext(TableContext);
    const { tableStore, prefixCls, dataSet, selectionMode, onRow, rowRenderer, parityRow, expandIconAsCell, expandedRowRenderer, isTree, canTreeLoadData, fullColumnWidth, expandRowByClick, } = context;
    const { highLightRow, selectedHighLightRow, mouseBatchChooseState, dragColumnAlign, rowDraggable, showRemovedRow, node, } = tableStore;
    const { id, key: rowKey } = record;
    const mounted = useRef(false);
    const dragRef = useRef(false);
    const needIntersection = intersectionRef;
    const disabled = isDisabledRow(record);
    const rowRef = useRef(null);
    const childrenRenderedRef = useRef();
    const needSaveRowHeight = isStickySupport() ? !isFixedRowHeight || tableStore.propVirtual || needIntersection : (!lock && (!isFixedRowHeight || iteratorSome(dataSet.fields.values(), field => field.get('multiLine', record))));
    const rowExternalProps = useComputed(() => ({
        ...(typeof rowRenderer === 'function' ? rowRenderer(record, index) : {}),
        ...(typeof onRow === 'function'
            ? onRow({
                dataSet,
                record,
                expandedRow: false,
                index,
            })
            : {}),
    }), [record, dataSet, index, onRow, rowRenderer]);
    const isLoading = tableStore.isRowPending(record);
    const isLoaded = tableStore.isRowLoaded(record);
    const isExpanded = tableStore.isRowExpanded(record);
    const isHover = tableStore.isRowHover(record);
    const expandable = (() => {
        const { isLeaf } = rowExternalProps;
        if (isLeaf === true) {
            return false;
        }
        return !!expandedRowRenderer || (isTree && (!!record.children || (canTreeLoadData && !isLoaded)));
    })();
    const setRowHeight = useCallback(action((key, height, target) => {
        if (inView && columnsInView && height && target.offsetParent) {
            if (metaData) {
                if (metaData.actualHeight !== height) {
                    tableStore.batchSetRowHeight(key, () => metaData.setHeight(height));
                }
            }
            else if (needIntersection) {
                if (record.getState(VIRTUAL_HEIGHT) !== height) {
                    record.setState(VIRTUAL_HEIGHT, height);
                }
            }
            if (!isStickySupport()) {
                if (get(tableStore.lockColumnsBodyRowsHeight, key) !== height) {
                    set(tableStore.lockColumnsBodyRowsHeight, key, height);
                }
            }
            tableStore.alignEditor();
        }
    }), [tableStore, metaData, inView, columnsInView, needIntersection, record]);
    const saveRef = useCallback(action((row) => {
        rowRef.current = row;
        if (needSaveRowHeight) {
            if (row) {
                setRowHeight(rowKey, row.offsetHeight, row);
            }
            else if (!isStickySupport() && get(tableStore.lockColumnsBodyRowsHeight, rowKey)) {
                remove(tableStore.lockColumnsBodyRowsHeight, rowKey);
            }
        }
        if (provided) {
            provided.innerRef(row);
        }
        if (needIntersection && typeof intersectionRef === 'function') {
            intersectionRef(row);
        }
    }), [rowRef, intersectionRef, needIntersection, needSaveRowHeight, rowKey, provided, setRowHeight]);
    const handleMouseEnter = useCallback(() => {
        if (highLightRow) {
            tableStore.setRowHover(record, true);
        }
    }, [highLightRow, tableStore, record]);
    const handleMouseLeave = useCallback(() => {
        if (highLightRow) {
            tableStore.setRowHover(record, false);
        }
    }, [highLightRow, tableStore, record]);
    const handleSelection = useCallback(() => {
        dataSet[record.isSelected ? 'unSelect' : 'select'](record);
    }, [record, dataSet]);
    const handleExpandChange = useCallback(() => {
        if (expandable) {
            tableStore.setRowExpanded(record, !isExpanded);
        }
    }, [tableStore, record, expandable, isExpanded]);
    const handleClick = useCallback(action((e) => {
        if (expandRowByClick) {
            handleExpandChange();
        }
        const { onClick } = rowExternalProps;
        if (highLightRow === HighLightRowType.click && !tableStore.rowClicked) {
            tableStore.rowClicked = true;
        }
        if (typeof onClick === 'function') {
            return onClick(e);
        }
    }), [tableStore, rowExternalProps, expandRowByClick, handleExpandChange]);
    const handleSelectionByClick = useCallback(async (e) => {
        if (await handleClick(e) !== false) {
            handleSelection();
        }
    }, [handleClick, handleSelection]);
    const handleSelectionByMouseDown = useCallback((e) => {
        handleSelection();
        const { onMouseDown } = rowExternalProps;
        if (typeof onMouseDown === 'function') {
            onMouseDown(e);
        }
    }, [handleSelection, rowExternalProps]);
    const handleSelectionByDblClick = useCallback((e) => {
        handleSelection();
        const { onDoubleClick } = rowExternalProps;
        if (typeof onDoubleClick === 'function') {
            onDoubleClick(e);
        }
    }, [handleSelection, rowExternalProps]);
    const focusRow = useCallback(() => {
        const { current } = rowRef;
        if (current) {
            if (!lock && !tableStore.editing) {
                const { element } = node;
                const { activeElement } = document;
                if (element &&
                    element.contains(activeElement) &&
                    (isStickySupport() ? !current.contains(activeElement) : Array.from(element.querySelectorAll(`tr[data-index="${record.id}"]`)).every(tr => !tr.contains(activeElement)))) {
                    current.focus();
                }
                // table 包含目前被focus的element
                // 找到当前组件对应record生成的组件对象 然后遍历 每个 tr里面不是focus的目标那么这个函数触发row.focus
            }
            if (tableStore.overflowY) {
                const { offsetParent } = current;
                if (offsetParent) {
                    const tableBodyWrap = offsetParent.parentNode;
                    if (tableBodyWrap) {
                        const { offsetTop, offsetHeight } = current;
                        const { scrollTop, offsetHeight: height } = tableBodyWrap;
                        const bottomSide = offsetTop + offsetHeight - height + measureScrollbar();
                        let st = scrollTop;
                        if (st < bottomSide) {
                            st = bottomSide;
                        }
                        if (st > offsetTop) {
                            st = offsetTop + 1;
                        }
                        if (st !== scrollTop) {
                            tableBodyWrap.scrollTop = st;
                            // node.handleBodyScrollTop({
                            //   target: tableBodyWrap,
                            //   currentTarget: tableBodyWrap,
                            // });
                        }
                    }
                }
            }
        }
    }, [rowRef, tableStore, lock, node]);
    // componentDidMount
    useEffect(() => {
        mounted.current = true;
        if (record.status === RecordStatus.add && tableStore.autoFocus) {
            const editor = tableStore.editors.values().next().value;
            if (editor && (isStickySupport() || getColumnLock(editor.props.column.lock) === getColumnLock(lock))) {
                const cell = findCell(tableStore, getColumnKey(editor.props.column), lock);
                if (cell) {
                    defer(() => cell.focus());
                }
            }
        }
        // componentWillUnmount
        return action(() => {
            mounted.current = false;
            /**
             * Fixed the when row resize has scrollbar the expanded row would be collapsed
             */
            if (!tableStore.isRowExpanded(record)) {
                tableStore.setRowExpanded(record, false, true);
            }
            if (!isStickySupport()) {
                remove(tableStore.lockColumnsBodyRowsHeight, rowKey);
            }
        });
    }, []);
    useEffect(() => {
        if (!isLoading && expandable && isExpanded && !isLoaded && tableStore.canTreeLoadData && !record.children) {
            tableStore.onTreeNodeLoad({ record });
        }
    }, [isLoading, expandable, isExpanded, isLoaded, tableStore, record]);
    const renderExpandRow = () => {
        if (expandable && (isExpanded || childrenRenderedRef.current)) {
            const expandRows = [];
            childrenRenderedRef.current = true;
            if (expandedRowRenderer) {
                const expandRowExternalProps = typeof onRow === 'function'
                    ? onRow({
                        dataSet,
                        record,
                        expandedRow: true,
                        index,
                    })
                    : {};
                const classString = classNames(`${prefixCls}-expanded-row`, expandRowExternalProps.className);
                const rowProps = {
                    key: `${rowKey}-expanded-row`,
                    className: classString,
                    style: { ...expandRowExternalProps.style },
                };
                if (!isStickySupport() && (tableStore.overflowX || !record.isCurrent)) {
                    rowProps.onMouseEnter = handleMouseEnter;
                    rowProps.onMouseLeave = handleMouseLeave;
                }
                if (!isExpanded) {
                    rowProps.hidden = true;
                }
                const Element = isExpanded || !parityRow ? 'tr' : 'div';
                const hasExpandTd = expandIconAsCell && !tableStore.expandIconColumnIndex;
                expandRows.push(React.createElement(Element, Object.assign({}, expandRowExternalProps, rowProps),
                    hasExpandTd && React.createElement("td", { className: `${prefixCls}-cell`, key: EXPAND_KEY }),
                    React.createElement("td", { key: `${EXPAND_KEY}-rest`, className: `${prefixCls}-cell`, colSpan: columnGroups.leafs.length - (hasExpandTd ? 1 : 0) },
                        React.createElement("div", { className: `${prefixCls}-cell-inner` }, expandedRowRenderer({ dataSet, record })))));
            }
            if (isValidElement(children)) {
                expandRows.push(cloneElement(children, { parentExpanded: isExpanded, key: `${rowKey}-expanded-rows` }));
            }
            return expandRows;
        }
        return [];
    };
    const renderExpandIcon = () => {
        const { expandIcon } = tableStore;
        if (typeof expandIcon === 'function') {
            return expandIcon({
                prefixCls,
                expanded: isExpanded,
                expandable,
                needIndentSpaced: !expandable,
                record,
                onExpand: handleExpandChange,
            });
        }
        if (tableStore.isRowPending(record)) {
            return React.createElement(Spin, { size: Size.small });
        }
        return (React.createElement(ExpandIcon, { prefixCls: prefixCls, expandable: expandable, onChange: handleExpandChange, expanded: isExpanded }));
    };
    const hasExpandIcon = (columnIndex) => (expandIconColumnIndex !== undefined && expandIconColumnIndex > -1 && (columnIndex + expandIconColumnIndex) === tableStore.expandIconColumnIndex);
    const getCell = (columnGroup, columnIndex, rest) => {
        const actualRecord = getRecord(columnGroup, groupPath, headerGroupIndex, record);
        const cellProps = {
            columnGroup,
            record: actualRecord,
            isDragging: snapshot ? snapshot.isDragging : false,
            isFixedRowHeight,
            provided: rest.key === DRAG_KEY ? provided : undefined,
            groupPath,
            rowIndex: virtualIndex === undefined ? index : virtualIndex,
            children: hasExpandIcon(columnIndex) ? renderExpandIcon() : undefined,
            isDragDisabled,
        };
        if (!isFixedRowHeight && virtualCell && !hidden) {
            return [
                React.createElement(TableVirtualCell, Object.assign({}, cellProps, rest, { key: rest.key })),
                actualRecord,
            ];
        }
        return [
            React.createElement(TableCell, Object.assign({}, cellProps, rest, { inView: inView, key: rest.key })),
            actualRecord,
        ];
    };
    const [columns, { isCurrent }] = (() => {
        const { customizable } = tableStore;
        const { leafs } = columnGroups;
        const columnLength = leafs.length;
        return leafs.reduce((result, columnGroup, columnIndex) => {
            const { key } = columnGroup;
            if (key !== CUSTOMIZED_KEY) {
                const colSpan = customizable && lock !== ColumnLock.left && (!rowDraggable || dragColumnAlign !== DragColumnAlign.right) && columnIndex === columnLength - 2 ? 2 : 1;
                const rest = {
                    key,
                    disabled,
                };
                if (colSpan > 1) {
                    rest.colSpan = colSpan;
                }
                const [cell, actualRecord] = getCell(columnGroup, columnIndex, rest);
                result[0].push(cell);
                if (highLightRow && actualRecord && actualRecord.isCurrent) {
                    result[1].isCurrent = true;
                }
            }
            return result;
        }, [[], {}]);
    })();
    useEffect(() => {
        if (isCurrent) {
            focusRow();
        }
    }, [isCurrent, focusRow]);
    const rowPrefixCls = `${prefixCls}-row`;
    const classString = classNames(rowPrefixCls, {
        [`${rowPrefixCls}-current`]: isCurrent && (highLightRow === HighLightRowType.click ? tableStore.rowClicked : highLightRow === HighLightRowType.focus ? node.isFocused : highLightRow),
        [`${rowPrefixCls}-hover`]: !isStickySupport() && highLightRow && isHover,
        [`${rowPrefixCls}-selected`]: selectedHighLightRow && isSelectedRow(record),
        [`${rowPrefixCls}-disabled`]: disabled,
        [`${rowPrefixCls}-mouse-batch-choose`]: mouseBatchChooseState && record.selectable && (tableStore.mouseBatchChooseIdList || []).includes(id),
        [`${rowPrefixCls}-expanded`]: expandable && isExpanded,
        [`${rowPrefixCls}-has-next`]: metaData && metaData.next,
    }, className, // 增加可以自定义类名满足拖拽功能
    rowExternalProps.className);
    const { style } = rowExternalProps;
    const rowProps = {
        ref: saveRef,
        className: classString,
        onClick: handleClick,
        tabIndex: -1,
        disabled,
        'data-index': id,
    };
    if (!isStickySupport() && tableStore.overflowX) {
        rowProps.onMouseEnter = handleMouseEnter;
        rowProps.onMouseLeave = handleMouseLeave;
    }
    if (hidden || (!showRemovedRow && record.status === RecordStatus.delete)) {
        rowProps.hidden = true;
    }
    const Element = hidden && parityRow ? 'div' : 'tr';
    if (selectionMode === SelectionMode.click) {
        rowProps.onClick = handleSelectionByClick;
    }
    else if (selectionMode === SelectionMode.dblclick) {
        rowProps.onDoubleClick = handleSelectionByDblClick;
    }
    else if (selectionMode === SelectionMode.mousedown) {
        rowProps.onMouseDown = handleSelectionByMouseDown;
    }
    const rowStyle = {
        ...style,
    };
    if (rowDraggable && provided) {
        Object.assign(rowProps, provided.draggableProps);
        Object.assign(rowStyle, provided.draggableProps.style, style);
        if (propStyle) {
            const { transform } = propStyle;
            if (transform) {
                if (rowStyle.transform) {
                    rowStyle.transform = 'none';
                }
                else {
                    rowStyle.transform = transform;
                    if (!dragRef.current) {
                        rowStyle.transition = 'none';
                        dragRef.current = true;
                    }
                }
            }
        }
        else {
            dragRef.current = false;
        }
        rowStyle.width = Math.max(tableStore.columnGroups.width, tableStore.width || 0);
        if (!dragColumnAlign) {
            rowStyle.cursor = 'move';
            Object.assign(rowProps, provided.dragHandleProps);
        }
    }
    const height = intersectionRef ? virtualHeight !== undefined ? virtualHeight :
        pxToRem(metaData ? metaData.height : record.getState(VIRTUAL_HEIGHT) || tableStore.virtualRowHeight, true) : lock ?
        pxToRem(get(tableStore.lockColumnsBodyRowsHeight, rowKey), true) : undefined;
    if (height) {
        rowStyle.height = height;
    }
    const useEmptyColumn = !fullColumnWidth && lock !== ColumnLock.left && !tableStore.overflowX && !tableStore.hasEmptyWidthColumn;
    if (useEmptyColumn) {
        columns.push(React.createElement("td", { key: "empty-column" }));
    }
    const tr = (React.createElement(Element, Object.assign({ key: rowKey }, rowExternalProps, rowProps, { style: rowStyle }), columns));
    return (React.createElement(React.Fragment, null,
        needSaveRowHeight ? (React.createElement(ResizeObservedRow, { onResize: setRowHeight, rowIndex: rowKey, key: rowKey }, tr)) : tr,
        renderExpandRow()));
};
TableRow.displayName = 'TableRow';
export default observer(TableRow);
